// @flow

import React from 'react';
import { get, isEmpty } from 'lodash';

import ErrorInfo from 'app/common/components/ErrorInfo';
import Errors from 'app/errors';
import InlineUpgradeTriggerModal from 'app/beta-editor/components/InlineUpgradeTrigger/InlineUpgradeTriggerContainer';
import LazyLoadDestinationAuthModal from 'app/common/components/LazyLoadDestinationAuthModal';
import LearnMoreErrorInfo from 'app/common/components/LearnMoreErrorInfo';
import Modal from 'app/common/components/Modal';
import UnpauseErrorModal from 'app/common/components/UnpauseErrorModal';
import { isDataFlowProtectionRequired } from 'app/common/utils/ErrorUtils';
import { selectErrorMessages } from 'app/common/error';

type ErrorModalProps = {
  onClose: () => void,
  errors: Array<Object>,
  isFromContactForm?: boolean,
};

const ErrorModal = ({
  onClose,
  errors,
  isFromContactForm,
}: ErrorModalProps) => {
  let error = errors[errors.length - 1];
  const upgradeError = errors.find(
    err => !isEmpty(get(err, ['errors', '0', 'reasons']))
  );

  if (upgradeError && upgradeError.status === 426) {
    const plans = get(upgradeError, ['errors', '0', 'plans']);
    const upgradeToPlan = get(plans, 'annual') || get(plans, 'monthly');

    if (upgradeToPlan) {
      return (
        <InlineUpgradeTriggerModal
          autoOpen={true}
          flowId={get(upgradeError, ['flowId'])}
          onClose={onClose}
          reason={get(upgradeError, [
            'errors',
            '0',
            'reasons',
            '0',
            'extension',
          ])}
          upgradeToPlan={upgradeToPlan}
        />
      );
    }
  }

  if (isDataFlowProtectionRequired(errors)) {
    return (
      <LazyLoadDestinationAuthModal
        checks={(error.json || {}).checks}
        flowId={error.flowId}
        onCloseModal={onClose}
      />
    );
  }

  if (
    error &&
    error.status === 403 &&
    error.name === 'UnpauseError' &&
    error.flowId
  ) {
    return (
      <UnpauseErrorModal
        checks={(error.json || {}).checks}
        flowId={error.flowId}
        onCloseModal={onClose}
      />
    );
  }

  let ErrorType = error && error.name && Errors[error.name];

  if (!ErrorType) {
    error = new Errors.UnknownError(error);
    ErrorType = Errors.UnknownError;
  }

  const summary = ErrorType.summary(error);
  const errorsFound = ErrorType.errors(error);
  const messages = selectErrorMessages(errorsFound);

  const content = ErrorType.hasHelpLink(error) ? (
    <LearnMoreErrorInfo
      continueText="or continue to Dashboard"
      helpLink={ErrorType.helpLink(error)}
      onClose={onClose}
      summary={ErrorType.summary(error)}
      title="There's an Error With This Zap"
    />
  ) : (
    <ErrorInfo
      isFromContactForm={isFromContactForm}
      messages={messages}
      status={error.status}
      summary={summary}
    />
  );

  return (
    <Modal
      closeOnEsc={true}
      closeOnOutsideClick={true}
      isOpened={true}
      onClose={onClose}
    >
      {content}
    </Modal>
  );
};

export default ErrorModal;
